import { IPromise } from 'angular';
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as _ from 'underscore';

import {
  ActivitySpinnerSize,
  ActivitySpinnerWidget,
  ButtonWidget,
  ButtonType,
  CheckBoxWithLabelWidget,
  CompactInlineAlertWidget,
  DialogPositioningBehavior,
  DialogType,
  InlineAlertType,
} from '@tableau/widgets';

import { Messages } from '../../../codegen/Localize';
import { ButtonWithSpinner } from '../../../common/react/components/ButtonWithSpinner';
import { SparseCachedList } from '../../../common/react/SparseCachedList';
import { Order } from '../../../ts/helpers/Order';
import { ServerFilterClauses } from '../../../ts/helpers/ServerFilterClauses';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { OrderKeys } from '../../../ts/models/OrderKeys';
import { ReactModalService } from '../../../ts/services/ReactModalService';
import { MapDispatchToProps } from '../../utils/ConnectUtils';
import { delayPromise } from '../../utils/Delay';
import { AddRemoveCollectionItemsData } from './CollectionModels';
import { CollectionsResourceQuery, CollectionItemsResourceQuery } from './Collections';
import { CollectionsDialogStyles } from './CollectionsDialogStyles';
import { ICollectionInfo } from './ICollectionInfo';
import { CreateEmptyCollectionAction, createEmptyCollectionAction } from '../../actions/spaces/ContentExplorerCreateActions';

export const ManageItemsInCollectionsDialogTestId = 'add-to-collections-dialog';
export const ManageItemsInCollectionsCreateButtonTestId = `${ManageItemsInCollectionsDialogTestId}-create`;
export const ManageItemsInCollectionsCancelButtonTestId = `${ManageItemsInCollectionsDialogTestId}-cancel`;
export const ManageItemsInCollectionsSaveButtonTestId = `${ManageItemsInCollectionsDialogTestId}-save`;
export const ManageItemsInCollectionsListContainerTestId = `${ManageItemsInCollectionsDialogTestId}-list-container`;
export const ManageItemsInCollectionsErrorTestId = `${ManageItemsInCollectionsDialogTestId}-error`;

export namespace ManageItemsInCollectionsDialog {
  export interface Options {
    readonly items: IContentInfo[];
    readonly targetCollectionLuids?: string[];
    readonly userId: string;
    readonly isAdmin: boolean;
  }

  interface ContentsDispatchProps {
    createEmptyCollection: (itemsToPossiblyAdd?: IContentInfo[]) => CreateEmptyCollectionAction;
  }

  export interface ContentsProps extends Options, ContentsDispatchProps {
    readonly closeDialog: () => void;
  }

  export interface ContentsState {
    readonly busy: boolean;
    readonly error: string;
    readonly collections: ICollectionInfo[];
    readonly collectionLuidToHasItemsMap: Map<string, boolean>;
  }

  export const CollectionsBlockSize = 1000;

  export class Contents extends React.PureComponent<ContentsProps, ContentsState> {
    private collectionsCache: SparseCachedList<ICollectionInfo, ICollectionInfo>;
    private isUnmounted = false;

    public constructor(props: ContentsProps) {
      super(props);

      this.state = {
        busy: false,
        error: undefined,
        collections: undefined, // undefined means not-yet-fetched
        collectionLuidToHasItemsMap: new Map<string, boolean>(),
      };
    }

    public componentDidMount() {
      this.updateCollectionsCacheWithNewQuery();
    }

    public componentDidUpdate(prevProps: ContentsProps) {
      if (prevProps.isAdmin !== this.props.isAdmin) {
        this.updateCollectionsCacheWithNewQuery();
      }
    }

    public componentWillUnmount() {
      this.isUnmounted = true;
    }

    private updateCollectionsCacheWithNewQuery() {
      // clear existing collections
      this.setState({
        collections: undefined,
        collectionLuidToHasItemsMap: new Map<string, boolean>(),
      });

      const filters = !this.props.isAdmin ? [ ServerFilterClauses.serverFilterClauses.owner(this.props.userId) ] : [];
      const query = new CollectionsResourceQuery()
        .withOrder([Order.desc(OrderKeys.updatedAt)])
        .withFilters(filters);
      this.collectionsCache = new SparseCachedList<ICollectionInfo, ICollectionInfo>(query, _.identity, CollectionsBlockSize);

      // fetch the first block of the cache after initialization
      this.fetchCollections(0, CollectionsBlockSize);
    }

    private handleScroll = async (e: React.UIEvent) => {
      const scrollTop = e.currentTarget.scrollTop;
      const clientHeight = e.currentTarget.clientHeight;
      const startIndex = Math.floor(scrollTop / CollectionsDialogStyles.COLLECTION_ITEM_HEIGHT);
      const itemsToFetch = Math.ceil(clientHeight / CollectionsDialogStyles.COLLECTION_ITEM_HEIGHT);
      const endIndex = startIndex + itemsToFetch;
      await this.fetchCollections(startIndex, endIndex);
    }

    public render(): JSX.Element {
      return (
        <>
          <div className={CollectionsDialogStyles.ItemsToAdd}>{this.getItemsToAddMessage()}</div>
          {this.getCollectionsListElement()}
          {!this.state.error && <div className={CollectionsDialogStyles.CreateCollectionContainer}>
            <ButtonWidget
              title={Messages.createCollection()}
              buttonType={ButtonType.LowEmphasis}
              testId={ManageItemsInCollectionsCreateButtonTestId}
              handleClick={this.handleCreateCollectionClicked}
            >
              {Messages.createCollection()}
            </ButtonWidget>
          </div>}
          <div className={CollectionsDialogStyles.ActionButtons}>
            <ButtonWithSpinner
              title={Messages.cancel()}
              buttonType={ButtonType.Outline}
              clicked={this.props.closeDialog}
              testId={ManageItemsInCollectionsCancelButtonTestId}
              disabled={this.state.busy}
            />
            <span className="tb-margin-left-wide">
              <ButtonWithSpinner
                title={Messages.save()}
                buttonType={ButtonType.Primary}
                clicked={this.saveChanges}
                testId={ManageItemsInCollectionsSaveButtonTestId}
                disabled={this.state.busy || !!this.state.error}
                showSpinner={this.state.busy}
              />
            </span>
          </div>
        </>
      );
    }

    private getItemsToAddMessage(): string {
      if (this.props.items.length > 1) {
        return Messages.addMultipleItemsToCollection({ ITEM_COUNT: String(this.props.items.length) });
      } else if (this.props.items.length === 1) {
        return Messages.addSingleItemToCollection({
          ITEM_TYPE: this.props.items[0].contentType,
          ITEM_NAME: this.props.items[0].name
        });
      }
      return undefined;
    }

    private async fetchCollections(startIndex: number, endIndex: number): Promise<void> {
      try {
        const collections = await this.collectionsCache.getItems(startIndex, endIndex);
        // TODO: Fetch whether the items are in these collections already (TFS 1096748)
        const targetCollectionLuids = this.props.targetCollectionLuids ?? [];
        const collectionLuidToHasItemsMap = new Map(this.state.collectionLuidToHasItemsMap);

        // Because the collections cache is sparse, filter out the unfetched collections before determining if they
        // should be selected or not.
        collections.filter(collection => collection)
          .forEach(collection => {
            // If the given collection is specified as a "target collection" (e.g., coming from the
            // 'Create Collection' dialog), start it off as checked. If it was previously checked, keep it checked
            const checked = targetCollectionLuids.includes(collection.luid)
              || collectionLuidToHasItemsMap.get(collection.luid);
            collectionLuidToHasItemsMap.set(collection.luid, checked);
          });

        !this.isUnmounted && this.setState({
          collections,
          collectionLuidToHasItemsMap,
        });
      } catch (e) {
        !this.isUnmounted && this.setState({ error: Messages.collectionsFailedToLoad() });
      }
    }

    private getCollectionsListElement(): JSX.Element | JSX.Element[] {
      if (this.state.error) {
        return (
          <CompactInlineAlertWidget
            testId={ManageItemsInCollectionsErrorTestId}
            type={InlineAlertType.Error}
          >
            {this.state.error}
          </CompactInlineAlertWidget>
        );
      } else if (!this.state.collections) {
        return (
          <div className={CollectionsDialogStyles.Spinner} >
            <ActivitySpinnerWidget
              spinnerSize={ActivitySpinnerSize.Small}
              testId="collections-list-spinner" />
          </div>
        );
      }

      const collectionCount = this.state.collections ? this.state.collections.length : 0;
      const totalHeight = CollectionsDialogStyles.COLLECTION_ITEM_HEIGHT * collectionCount;
      return (
        <div
          className={CollectionsDialogStyles.CollectionsList}
          onScroll={this.handleScroll}
          data-tb-test-id={ManageItemsInCollectionsListContainerTestId}
        >
          <div style={{ height: totalHeight }}>
            {this.state.collections.map((collection, idx) => this.getCheckbox(collection, idx))}
          </div>
        </div>
      );
    }

    private getCheckbox(collection: ICollectionInfo, idx: number) {
      // Because the collections cache can be sparse, unfetched collections should be rendered as a blank row until
      // they are fetched.
      if (!collection) {
        return <DummyCollectionRow key={`Manage-Items-Dialog-Checkbox-Dummy-Collection-${idx}`}/>;
      }
      return (
        <CheckBoxWithLabelWidget
          checked={this.state.collectionLuidToHasItemsMap.get(collection.luid) ?? false}
          handleChange={checked => this.updateItemIsInCollectionMap(collection.luid, checked)}
          label={collection.name}
          testId={`${collection.name}-collection-checkbox`}
          containerStyle={CollectionsDialogStyles.CheckBoxContainer}
          key={`Manage-Items-Dialog-Checkbox-Collection-${collection.name}-${collection.luid}`}
        />
      );
    }

    private saveChanges = async () => {
      // TODO: This currently just calls "addItemsToCollection" for every checked collection, regardless of whether or
      //   not the item is already in the collection. It also does not remove items from any collections (TFS 1037596).
      const promises: Promise<AddRemoveCollectionItemsData>[] = [];
      this.state.collectionLuidToHasItemsMap.forEach((checked, luid) => {
        if (checked) {
          promises.push(addItemsToCollection(luid, this.props.items));
        }
      });

      try {
        this.setState({ busy: true });
        !this.isUnmounted && await Promise.all(promises);
      } catch {
        //TODO: Error handling! (TFS 1096749)
      } finally {
        !this.isUnmounted && this.setState({ busy: false });
        await delayPromise(100); // Delay a short amount so that any action toast will appear before the dialog closes. It looks better this way.
        this.props.closeDialog();
      }
    }

    private updateItemIsInCollectionMap(luid: string, checked: boolean) {
      const collectionLuidToHasItemsMap = new Map(this.state.collectionLuidToHasItemsMap);
      collectionLuidToHasItemsMap.set(luid, checked);
      !this.isUnmounted && this.setState({ collectionLuidToHasItemsMap });
    }

    private handleCreateCollectionClicked = () => {
      this.props.createEmptyCollection(this.props.items);
      this.props.closeDialog();
    };
  }

  export const DummyCollectionRow = () => <div style={CollectionsDialogStyles.CheckBoxContainer}/>;

  const mapContentsDispatchProps: MapDispatchToProps<ContentsDispatchProps> = dispatch => bindActionCreators({
    createEmptyCollection: createEmptyCollectionAction,
  }, dispatch);

  export const ConnectedContents = connect(null, mapContentsDispatchProps)(Contents);

  async function addItemsToCollection(luid: string, items: IContentInfo[]): Promise<AddRemoveCollectionItemsData> {
    const query = new CollectionItemsResourceQuery(luid);
    return query.addItemsToCollection(items);
  }

  export function open(opts: Options): IPromise<any> {
    const closeDialog = () => modalInstance && modalInstance.close();
    const modalInstance = ReactModalService.open({
      title: Messages.addToCollectionsDialogTitle(),
      testId: ManageItemsInCollectionsDialogTestId,
      type: DialogType.NormalWithTitleBar,
      showCloseButton: false,
      positioningBehavior: DialogPositioningBehavior.Centered,
      dialogWidth: CollectionsDialogStyles.ADD_TO_DIALOG_WIDTH,
      dialogBodyStyle: { maxWidth: CollectionsDialogStyles.ADD_TO_DIALOG_WIDTH + 'px' },
      dialogContent: <ConnectedContents {...opts} closeDialog={closeDialog} />,
    });
    return modalInstance.result;
  }
}
